import { cn } from "cn";
import { Link, NavLink } from "react-router-dom";

import { canManageRequests, holds, type Viewer } from "../api/client";
import { Button } from "./ui/button";

/**
 * HR destinations first and always; the operator surface is one link at the end
 * of the row rather than the shape of the whole application. An `HR` token
 * ranks below `Auditor`, so the link is absent for the audience that would only
 * be refused by it. The server decides either way.
 */
const HR_ROUTES: [to: string, label: string][] = [
  ["/people", "People"],
  ["/teams", "Teams"],
  ["/requests", "Requests"],
  ["/roles", "Roles"],
];

export function Masthead({
  signedIn,
  viewer,
  onSignIn,
  onSignOut,
}: {
  signedIn: boolean;
  viewer: Viewer | null;
  onSignIn: () => void;
  onSignOut: () => void;
}) {
  const link = ({ isActive }: { isActive: boolean }) =>
    cn(
      "border-b-2 pb-1.5 text-[0.9375rem] transition-colors",
      isActive
        ? "border-ink font-bold text-ink"
        : "border-transparent font-medium text-label hover:text-signal",
    );

  return (
    <header className="mb-6">
      <div className="flex flex-wrap items-center gap-x-8 gap-y-4">
        <div className="flex flex-col gap-2">
          <span aria-hidden className="h-1 w-10 bg-signal" />
          <h1 className="text-[1.3125rem] font-extrabold tracking-[-0.03em]">
            People &amp; access
          </h1>
        </div>
        <nav className="flex flex-1 flex-wrap items-center gap-x-5 gap-y-2 pb-0.5">
          {signedIn && (
            <>
              {HR_ROUTES.map(([to, label]) => (
                <NavLink key={to} to={to} className={link}>
                  {label}
                </NavLink>
              ))}
              {holds(viewer, "Auditor") && <NavLink to="/access" className={link}>Apps &amp; access</NavLink>}
              {holds(viewer, "Auditor") && (
                <NavLink to="/it/runs" className={link}>
                  IT &amp; audit
                </NavLink>
              )}
            </>
          )}
        </nav>
        <div className="flex items-center gap-3 pb-0.5">
          {signedIn && canManageRequests(viewer) && <Button asChild size="sm"><Link to="/requests/new">New request</Link></Button>}
          {viewer && (
            <span className="hidden text-xs text-quiet xl:inline">{viewer.upn ?? viewer.object_id}</span>
          )}
          <Button variant="outline" size="sm" onClick={signedIn ? onSignOut : onSignIn}>
            {signedIn ? "Sign out" : "Sign in"}
          </Button>
        </div>
      </div>
      <div aria-hidden className="mt-4 h-px bg-rule" />
    </header>
  );
}
